import { kUtf8TextCore } from "../../../Admin/kReusableCoreTS/kUtf8TextCore";
import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppDocItems } from "./AppDocItems";
import { AppFileHref } from "./AppFileHref";
import { IkDomElement, IkDomNode } from "../../../Admin/kReusableCoreTS/kHtmlParserCore";
import { kHtmlDomWriterCore } from "../../../Admin/kReusableCoreTS/kHtmlDomWriterCore";
import { kHtmlSafeCore, kHtmlSafePolicy, kHtmlSafeResult } from "../../../Admin/kReusableCoreTS/kHtmlSafeCore";
import { AppAiChatDoc } from "./AppAiChat";
import { AppAssistApi } from "./AppAssistApi";
import { AppAssistExport } from "./AppAssistExport";
import { AppAssistRich } from "./AppAssistRich";
import { AppAssistBlock, AppAssistEntry, AppAssistRules, AppAssistSpan } from "./AppAssistRules";

// AppPins - `pins.json`: the tables and charts the owner KEPT from AI answers for ONE contact ("Keep this",
// owner-approved 2026-09-27), kept beside `facts.json`, `tasks.json` and `ai-chat.json` in that person's
// folder in the subscriber's own Dropbox, and shown in the contact page's Insights drawer ("Kept from AI").
//
// ⚠ RULES ONLY - NO `XMLHttpRequest`, NO `window`, NO `node:` import. The SERVICE writes the file (`PinRun`,
// inside `/assist`'s `pin`/`unpin` actions) and the PAGE GENERATOR reads it (`DossierHtml`); both use THIS
// reader, THIS serialiser and THIS block rule, so the halves can never disagree (`PinContract.test.ts`).
//
//     { "schema": 1, "personKey": "+46...", "items": [ { "id": "p-<12 hex>", "at": ISO-8601 UTC,
//       "kind": "chart" | "table", "title": "...", "html": "<cleaned markup>",
//       "fromItem": "c-<12 hex>" (the ai-chat.json answer it came from), "block": 0.. (which block of it) } ] }
//
// ==================================================================================================
// !! ⛔ THE MARKUP IS NEVER THE PAGE'S: IT IS RE-DERIVED FROM THE SAVED ANSWER, AND CLEANED TWICE
// ==================================================================================================
//
// The AI panel runs beside the `browser` archive key, which can SEND as the owner, and a contact's messages
// can steer what the model writes. So a pin request carries only `{id, block}`: the service finds that
// answer in the contact's OWN `ai-chat.json`, takes block `block` of its text by the panel's own splitter
// (`AppAssistRules.Blocks` - the index both halves count with), and cleans it with the shared allow-list
// (`Derive`). The page generator cleans `html` AGAIN when it renders (`Render`), with a per-pin id prefix,
// because the file is in the subscriber's Dropbox and anyone with the folder can edit it.
// ⚠ A pipe table (Markdown) is turned into a `<table>` here from its PARSED cells - every word escaped -
// and then cleaned like any fence, so there is one kind of stored markup.
//
// ⛔ THE FILE IS OUTSIDE INPUT; THE READER IS STRICT (`AppAiChat`'s rule and reason): one malformed item
// makes the whole document `Unreadable`, because skipping it would let the next pin write the file back
// WITHOUT it. An unreadable file is fate 1: the page shows no kept items, the service NEVER writes over it.
// ⛔ THE CAP REFUSES, IT NEVER DROPS: `MaxItems` pins and `MaxBytes` of file. Dropping the oldest (the
// history's rule) would silently delete something the owner chose to keep; the owner removes one instead.
// ⚠ PRIVATE DATA (numbers about a person). Nothing here or in its callers is ever logged.

/** One kept table or chart. */
export class AppPinItem {
    public Id: string = "";
    /** ISO-8601, UTC - when it was kept. */
    public At: string = "";
    /** `chart` or `table`. */
    public Kind: string = "";
    public Title: string = "";
    /** The CLEANED markup, one line. ⛔ Cleaned again wherever it is drawn. */
    public Html: string = "";
    /** The `ai-chat.json` answer it came from (`c-` + 12 hex). */
    public FromItem: string = "";
    /** Which block of that answer (`AppAssistRules.Blocks` index). */
    public Block: number = 0;
}

/** One contact's kept items, read. */
export class AppPinDoc {
    /** The file was there. */
    public Found: boolean = false;
    /** The file was there and could not be read - never written over. */
    public Unreadable: boolean = false;
    public PersonKey: string = "";
    public Items: AppPinItem[] = [];
}

/** The block of an answer a pin points at: `rich` (a fence's source) or `table` (a pipe table), else "". */
export class AppPinSource {
    public Kind: string = "";
    /** Markup to clean: the fence's own source, or the pipe table built as escaped `<table>` markup. */
    public Source: string = "";
}

export class AppPins {
    public static readonly FileName: string = "pins.json";
    public static readonly Schema: number = 1;
    public static readonly Kinds: string[] = ["chart", "table"];
    public static readonly IdPattern: RegExp = /^p-[0-9a-f]{12}$/;
    /** The `action` values of `/assist` that change the file - ONE source, `AppAssistApi` (see its note on the cycle). */
    public static readonly PinAction: string = AppAssistApi.PinAction;
    public static readonly UnpinAction: string = AppAssistApi.UnpinAction;

    /** ⛔ THE CAP: at most this many kept items, and this much file - REFUSED past it, never trimmed. */
    public static readonly MaxItems: number = 20;
    public static readonly MaxBytes: number = 200000;
    /** One pin's markup - an answer's whole rich budget (`AppAssistRich.RichMaxChars`). */
    public static readonly MaxHtml: number = AppAssistRich.RichMaxChars;
    public static readonly MaxTitle: number = 120;
    /** The highest block index a request may name (`AppAssistApi.MaxPinBlock`). */
    public static readonly MaxBlock: number = AppAssistApi.MaxPinBlock;
    /** Every id a drawn pin keeps starts with this, then the pin's own id - one page, many pins. */
    public static readonly IdPrefix: string = "wac-pin-";

    /**
     * `People/Tomo/pins.json` from a page href `People/Tomo/Tomo.html`, or "" for an href with no folder.
     * DOCUMENTED EXCEPTION: pure string arithmetic. ERROR-SUPPRESSED-JUSTIFIED: "" is the answer.
     */
    public static FileOf = (Href: string): string => {
        return AppFileHref.FileOf(Href, AppPins.FileName);
    };

    /**
     * The text as a document. ⛔ STRICT - see the header. "" is an empty, readable document (no file).
     * ⛔ FATE 1, NEVER THE HANDLER: an unreadable file is an expected condition.
     */
    public static Parse = (Text: string): AppPinDoc => {
        return AppDocItems.Open(Text, AppPins.ObjectIn, AppPins.Schema, (): AppPinDoc => new AppPinDoc(), AppPins.Bad, AppPins.ItemIn, new AppPinDoc(), "AppPins.Parse");
    };

    /** One item, or null when it is not exactly the shape this build writes. */
    private static ItemIn = (Raw: unknown): AppPinItem | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (Raw === null || typeof Raw !== "object" || Array.isArray(Raw) === true) { return null; }
            const Bag: Record<string, unknown> = Raw as Record<string, unknown>;
            if (typeof Bag.id !== "string" || AppPins.IdPattern.test(Bag.id as string) === false) { return null; }
            if (typeof Bag.at !== "string" || isNaN(Date.parse(Bag.at as string)) === true) { return null; }
            if (typeof Bag.kind !== "string" || AppPins.Kinds.indexOf(Bag.kind as string) < 0) { return null; }
            if (typeof Bag.title !== "string" || (Bag.title as string).length > AppPins.MaxTitle) { return null; }
            if (typeof Bag.html !== "string" || (Bag.html as string).length > AppPins.MaxHtml) { return null; }
            if (typeof Bag.fromItem !== "string" || /^c-[0-9a-f]{12}$/.test(Bag.fromItem as string) === false) { return null; }
            if (AppPins.BlockOk(Bag.block) === false) { return null; }
            const Out: AppPinItem = new AppPinItem();
            Out.Id = Bag.id as string;
            Out.At = Bag.at as string;
            Out.Kind = Bag.kind as string;
            Out.Title = Bag.title as string;
            Out.Html = Bag.html as string;
            Out.FromItem = Bag.fromItem as string;
            Out.Block = Bag.block as number;
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPins.ItemIn");
            return null;
        }
    };

    /**
     * A block index: a whole number from 0 to `MaxBlock`.
     * DOCUMENTED EXCEPTION: pure check of a value in hand. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
     */
    public static BlockOk = (Value: unknown): boolean => {
        return AppAssistApi.PinBlockOk(Value);
    };

    /** The document back out as the file's text - the ONE serialiser. */
    public static Json = (Doc: AppPinDoc): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Items: Record<string, unknown>[] = [];
            for (const One of Doc.Items) {
                Items.push({ id: One.Id, at: One.At, kind: One.Kind, title: One.Title, html: One.Html,
                    fromItem: One.FromItem, block: One.Block });
            }
            return JSON.stringify({ schema: AppPins.Schema, personKey: nz(Doc.PersonKey), items: Items }, null, 1);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPins.Json");
            return "";
        }
    };

    /** The pin already kept from this block of this answer, or null. */
    public static Find = (Doc: AppPinDoc, FromItem: string, Block: number): AppPinItem | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            for (const One of Doc.Items) { if (One.FromItem === nz(FromItem) && One.Block === Block) { return One; } }
            return null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPins.Find");
            return null;
        }
    };

    /**
     * Add `Item`. Answers "added", "same" (this block is already kept - nothing to write), or "full" (the cap -
     * nothing changed). ⛔ REFUSED PAST THE CAP, NEVER TRIMMED - see the header.
     */
    public static Add = (Doc: AppPinDoc, Item: AppPinItem): string => {
        if (getCoreUtils().KErrorMode === true) { return "full"; }
        let AddedAt: number = -1;
        try {
            if (AppPins.Find(Doc, Item.FromItem, Item.Block) !== null) { return "same"; }
            if (Doc.Items.length >= AppPins.MaxItems) { return "full"; }
            const NextAt: number = Doc.Items.length;
            Doc.Items.push(Item);
            AddedAt = NextAt;
            const Text: string = AppPins.Json(Doc);
            if (getCoreUtils().KErrorMode === true || Text.length === 0 || AppPins.BytesOf(Text) > AppPins.MaxBytes) {
                AppPins.UndoAdd(Doc, Item, AddedAt);
                return "full";
            }
            return "added";
        } catch (e) {
            AppPins.UndoAdd(Doc, Item, AddedAt);
            getCoreUtils().KGlobalErrorHandler(e, "AppPins.Add");
            return "full";
        }
    };

    /** Roll back only this tentative insertion. DOCUMENTED EXCEPTION: cleanup must run through an error gate. */
    private static UndoAdd = (Doc: AppPinDoc, Item: AppPinItem, AddedAt: number): void => {
        if (AddedAt < 0) { return; }
        let At: number = AddedAt;
        if (Doc.Items[At] !== Item) { At = Doc.Items.lastIndexOf(Item); }
        if (At >= 0) { Doc.Items.splice(At, 1); }
    };

    /** Remove pin `Id`. Answers "removed", or "gone" when it is not there (a second click - nothing to write). */
    public static Remove = (Doc: AppPinDoc, Id: string): string => {
        if (getCoreUtils().KErrorMode === true) { return "gone"; }
        try {
            for (let At: number = 0; At < Doc.Items.length; At = At + 1) {
                if (Doc.Items[At].Id === nz(Id)) { Doc.Items.splice(At, 1); return "removed"; }
            }
            return "gone";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPins.Remove");
            return "gone";
        }
    };

    /** UTF-8 size; Infinity refuses an unmeasurable nonempty file. DOCUMENTED EXCEPTION: pure fail-stop measure. */
    public static BytesOf = (Text: string): number => {
        const One: string = nz(Text);
        const Bytes: number = kUtf8TextCore.ByteLength(One);
        // A gated or failed measure is not permission to admit a nonempty file past the cap.
        if (Bytes === 0 && One.length > 0) { return Infinity; }
        return Bytes;
    };

    /**
     * Block `Block` of an answer's text, when it is one that can be kept (a ```wac-html fence or a pipe
     * table); `Kind` "" otherwise. ⛔ THE INDEX IS `AppAssistRules.Blocks`' - the panel counts with it too.
     */
    public static SourceOf = (Text: string, Block: number): AppPinSource => {
        if (getCoreUtils().KErrorMode === true) { return new AppPinSource(); }
        try {
            const Out: AppPinSource = new AppPinSource();
            const All: AppAssistBlock[] = AppAssistRules.Blocks(nz(Text));
            if (AppPins.BlockOk(Block) === false || Block >= All.length) { return Out; }
            const One: AppAssistBlock = All[Block];
            if (One.Kind === "rich") { Out.Kind = "rich"; Out.Source = One.Source; }
            if (One.Kind === "table") { Out.Kind = "table"; Out.Source = AppPins.TableMarkup(One); }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPins.SourceOf");
            return new AppPinSource();
        }
    };

    /** A pipe table as `<table>` markup: its PARSED cells, every word escaped. It is cleaned after this. */
    private static TableMarkup = (Block: AppAssistBlock): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Out: string[] = ["<table><thead><tr>"];
            for (const Cell of Block.Head) { Out.push("<th>" + AppPins.CellMarkup(Cell) + "</th>"); }
            Out.push("</tr></thead><tbody>");
            for (const Row of Block.Rows) {
                Out.push("<tr>");
                for (const Cell of Row) { Out.push("<td>" + AppPins.CellMarkup(Cell) + "</td>"); }
                Out.push("</tr>");
            }
            Out.push("</tbody></table>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPins.TableMarkup");
            return "";
        }
    };

    /** One cell's runs as escaped text (bold/italic/code marks dropped - a kept table is its words). */
    private static CellMarkup = (Cell: AppAssistSpan[]): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Words: string = "";
            for (const Span of Cell) { Words = Words + nz(Span.Text); }
            return kHtmlDomWriterCore.EscapeText(Words);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPins.CellMarkup");
            return "";
        }
    };

    /**
     * Markup cleaned by the shared AI-answer allow-list (`AppAssistRich.Policy`), as one line, or null when
     * nothing safe is left or a limit refused it. `Prefix` "" at the service (no id prefix stored); the page
     * passes the pin's own prefix when it draws.
     */
    public static Clean = (Source: string, Prefix: string): kHtmlSafeResult | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const Policy: kHtmlSafePolicy = AppAssistRich.Policy();
            Policy.IdPrefix = nz(Prefix);
            const Result: kHtmlSafeResult = kHtmlSafeCore.Clean(nz(Source), Policy);
            if (Result.Root === null || Result.Refused !== "" || Result.Root.childNodes.length === 0) { return null; }
            return Result;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPins.Clean");
            return null;
        }
    };

    /** The cleaned result as one line of markup (`kHtmlSafeCore.ToHtml`), "" for none. */
    public static HtmlOf = (Result: kHtmlSafeResult | null): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Result === null) { return ""; }
            return kHtmlSafeCore.ToHtml(Result).replace(/\s*[\r\n]+\s*/g, " ").trim();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPins.HtmlOf");
            return "";
        }
    };

    /**
     * ⭐ THE PIN, RE-DERIVED FROM THE SAVED ANSWER - never from the page. `null` when item `FromItem` is not in
     * `History`, block `Block` of its entry's answer is not a table or chart, or nothing safe survives cleaning.
     * ⛔ THE ENTRY IS `AppAssistRules.FromHistory`'S - the very text the panel draws and counts blocks in (an
     * entry's answer items joined), so block N here is block N there. `NowIso` is when it was kept; `Id` its id.
     */
    public static Derive = (History: AppAiChatDoc, FromItem: string, Block: number, NowIso: string, Id: string): AppPinItem | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const Entry: AppAssistEntry | null = AppPins.EntryOf(History, FromItem);
            if (Entry === null) { return null; }
            const Source: AppPinSource = AppPins.SourceOf(Entry.Answer, Block);
            if (Source.Kind === "") { return null; }
            const Result: kHtmlSafeResult | null = AppPins.Clean(Source.Source, "");
            const Html: string = AppPins.HtmlOf(Result);
            if (Result === null || Html.length === 0 || Html.length > AppPins.MaxHtml) { return null; }
            const Out: AppPinItem = new AppPinItem();
            Out.Id = nz(Id);
            Out.At = nz(NowIso);
            Out.Kind = AppPins.KindOf(Result.Root as IkDomElement);
            Out.Title = AppPins.TitleFor(Result.Root as IkDomElement, Out.Kind, Entry.Ask);
            Out.Html = Html;
            Out.FromItem = nz(FromItem);
            Out.Block = Block;
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPins.Derive");
            return null;
        }
    };

    /** The panel entry (`AppAssistRules.FromHistory`) holding item `Id`, answered and not a closed task's, or null. */
    private static EntryOf = (History: AppAiChatDoc, Id: string): AppAssistEntry | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (History.Unreadable === true || nz(Id).length === 0) { return null; }
            for (const Entry of AppAssistRules.FromHistory(History)) {
                if (Entry.Ids.indexOf(nz(Id)) >= 0 && Entry.TaskId.length === 0 && Entry.Answer.length > 0) { return Entry; }
            }
            return null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPins.EntryOf");
            return null;
        }
    };

    /**
     * The id a pin request names for an entry: its LAST item (the answer - an entry's ids are in file order).
     * DOCUMENTED EXCEPTION: pure read of a value in hand. ERROR-SUPPRESSED-JUSTIFIED: "" = nothing to name.
     */
    public static ItemOf = (Ids: string[]): string => {
        if (Ids.length === 0) { return ""; }
        return nz(Ids[Ids.length - 1]);
    };

    /** `table` when the cleaned block's first element is a table, else `chart`. */
    private static KindOf = (Root: IkDomElement): string => {
        if (getCoreUtils().KErrorMode === true) { return "chart"; }
        try {
            const First: IkDomElement | null = AppPins.FirstElement(Root);
            if (First !== null && kHtmlSafeCore.TagOf(First) === "table") { return "table"; }
            return "chart";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPins.KindOf");
            return "chart";
        }
    };

    /**
     * What the kept item is called: a chart's own `<title>`, a table's `<caption>`, else the question that
     * produced it, else "Chart"/"Table" - spaces collapsed, at most `MaxTitle` characters. Text, never markup.
     */
    private static TitleFor = (Root: IkDomElement, Kind: string, Ask: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Title: string = "";
            const First: IkDomElement | null = AppPins.FirstElement(Root);
            if (First !== null && Kind === "chart") { Title = AppAssistExport.TitleOf(First); }
            if (First !== null && Kind === "table") { Title = AppPins.CaptionOf(First); }
            if (Title.length === 0) { Title = nz(Ask).replace(/\s+/g, " ").trim(); }
            if (Title.length === 0 && Kind === "table") { Title = AppAssistExport.TableName; }
            if (Title.length === 0) { Title = AppAssistExport.ChartName; }
            if (Title.length > AppPins.MaxTitle) { Title = Title.substring(0, AppPins.MaxTitle - 1).trim() + "…"; }
            return Title;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPins.TitleFor");
            return "";
        }
    };

    /** A table's own `<caption>` text, or "". */
    private static CaptionOf = (Table: IkDomElement): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            for (const Kid of Table.childNodes) {
                if (Kid.nodeType === 1 && kHtmlSafeCore.TagOf(Kid as IkDomElement) === "caption") {
                    return AppAssistExport.TextOf(Kid).replace(/\s+/g, " ").trim();
                }
            }
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPins.CaptionOf");
            return "";
        }
    };

    /** The first element child of `Root`, or null. */
    private static FirstElement = (Root: IkDomElement): IkDomElement | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            for (const Kid of Root.childNodes as IkDomNode[]) { if (Kid.nodeType === 1) { return Kid as IkDomElement; } }
            return null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPins.FirstElement");
            return null;
        }
    };

    /**
     * ⛔ A STORED PIN AS PAGE MARKUP - CLEANED AGAIN, with the pin's own id prefix, and serialised by the shared
     * writer. "" when nothing safe is left (the page then shows only the pin's title).
     */
    public static Render = (Item: AppPinItem): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Prefix: string = AppPins.IdPrefix + nz(Item.Id).replace(/[^A-Za-z0-9-]/g, "") + "-";
            return AppPins.HtmlOf(AppPins.Clean(Item.Html, Prefix));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPins.Render");
            return "";
        }
    };

    /**
     * DOCUMENTED EXCEPTION: pure parse probe over text in hand.
     * ERROR-SUPPRESSED-JUSTIFIED: null IS the answer for text that is not a JSON object - `Parse` turns it
     * into `Unreadable`, the defined fate (see the header).
     */
    private static ObjectIn = (Text: string): Record<string, unknown> | null => {
        try {
            const Raw: unknown = JSON.parse(Text);
            if (Raw === null || typeof Raw !== "object" || Array.isArray(Raw) === true) { return null; }
            return Raw as Record<string, unknown>;
        } catch (e) {
            return null;
        }
    };

    /** DOCUMENTED EXCEPTION: constant safe-default factory - the unreadable document. */
    private static Bad = (): AppPinDoc => {
        const Out: AppPinDoc = new AppPinDoc();
        Out.Found = true;
        Out.Unreadable = true;
        return Out;
    };
}
